"use client";

import { useSession, signOut } from "next-auth/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import {
  LogOut,
  Truck,
  User,
  // SunMoon,
  ShoppingCart,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerClose,
} from "@/components/ui/drawer";
import { Skeleton } from "@/components/ui/skeleton";
// import { ThemeToggle } from "@/components/theme/theme-toggle";
import { useCartStore } from "@/store/cart-store";
import { toast } from "@/hooks/use-toast";
import { Separator } from "../ui/separator";

export const UserButton = () => {
  const { data: session, status } = useSession();
  const router = useRouter();
  const isMobile = useIsMobile();

  // Show skeleton loader while session is loading
  if (status === "loading") {
    return (
      <div className='flex items-center justify-center'>
        <Skeleton className='h-7 w-7 rounded-full' />
      </div>
    );
  }

  // If no user exists, return null (component won't render)
  if (!session?.user) {
    return null;
  }

  const handleSignOut = async () => {
    try {
      // Dispatch the sign-out event FIRST to show overlay immediately
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("user-signout"));
      }

      toast({
        title: "Signing out",
        description: "Clearing your session and cart data...",
      });

      // First, clear all cart data from Zustand store - do this synchronously
      const cartStore = useCartStore.getState();
      cartStore.clearAllCarts();

      // Clear localStorage data directly and synchronously
      if (typeof window !== "undefined") {
        // Directly remove the items - put this before signOut to ensure it happens
        window.localStorage.removeItem("user-carts");
        window.localStorage.removeItem("active-cart-data");
        window.localStorage.removeItem("cookie-consent-status");
      }

      // No need for timeout, sign out immediately after clearing
      await signOut({
        redirect: false,
        callbackUrl: window.location.origin,
      });

      // Force a complete page reload to clear any in-memory state
      window.location.href = window.location.origin;
    } catch (error) {
      console.error("[UserButton] Error during sign out:", error);

      // Even if there's an error, try to clear storage anyway
      if (typeof window !== "undefined") {
        window.localStorage.removeItem("user-carts");
        window.localStorage.removeItem("active-cart-data");
      }

      // Fallback to direct navigation in case of error
      window.location.href = window.location.origin;
    }
  };

  const UserProfile = () => (
    <div className='my-2 p-4 flex flex-col gap-1 items-center'>
      {session.user.image && (
        <div className='w-20 h-20 mb-4 relative overflow-hidden rounded-full'>
          <Image
            src={session.user.image}
            alt={session.user.firstName || "User profile"}
            fill
            className='object-cover'
            sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
          />
        </div>
      )}
      <p className='font-bold text-xs'>
        {session.user.firstName} {session.user.lastName}
      </p>
      <span className='text-xs font-medium text-secondary-foreground'>
        {session.user.email}
      </span>
    </div>
  );

  const DesktopContent = () => (
    <>
      <UserProfile />
      <DropdownMenuSeparator />

      {session.user.role !== "user" && (
        <DropdownMenuItem
          onClick={() => router.push("/dashboard")}
          className='group py-2 font-medium cursor-pointer ease-in-out'
        >
          <User
            size={14}
            className='mr-3 group-hover:scale-125 transition-all duration-300 ease-in-out'
          />
          My Dashboard
        </DropdownMenuItem>
      )}

      <DropdownMenuItem
        onClick={() => router.push("/orders")}
        className='group py-2 font-medium cursor-pointer ease-in-out'
      >
        <Truck
          size={14}
          className='mr-3 group-hover:translate-x-1 transition-all duration-300 ease-in-out'
        />
        My Orders
      </DropdownMenuItem>

      <DropdownMenuItem
        onClick={() => router.push("/quotes")}
        className='group py-2 font-medium cursor-pointer ease-in-out'
      >
        <ShoppingCart
          size={14}
          className='mr-3 group-hover:translate-x-1 transition-all duration-300 ease-in-out'
        />
        My Quotes
      </DropdownMenuItem>

      <DropdownMenuItem
        onClick={() => router.push("/account")}
        className='group py-2 font-medium cursor-pointer ease-in-out'
      >
        <User
          size={14}
          className='mr-3 group-hover:scale-125 transition-all duration-300 ease-in-out'
        />
        My Account
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      {/* <div className='px-2 py-1.5'>
        <div className='flex items-center'>
          <SunMoon size={14} className='mr-3' />
          <span className='text-sm font-medium'>Theme</span>
          <div className='ml-auto'>
            <ThemeToggle variant='ghost' />
          </div>
        </div>
      </div>

      <DropdownMenuSeparator /> */}

      <DropdownMenuItem
        onClick={handleSignOut}
        className='py-2 group focus:bg-destructive font-medium cursor-pointer'
      >
        <LogOut
          size={14}
          className='mr-3 group-hover:scale-75 transition-all duration-300 ease-in-out'
        />
        Sign out
      </DropdownMenuItem>
    </>
  );

  const MobileContent = () => (
    <div className='flex flex-col'>
      <UserProfile />
      <div className='flex flex-col gap-1'>
        <Separator />

        {session.user.role !== "user" && (
          <DrawerClose asChild>
            <Button
              onClick={() => router.push("/dashboard")}
              variant='ghost'
              className='w-full justify-start font-medium'
            >
              <User
                size={14}
                className='mr-3 group-hover:scale-125 transition-all duration-300 ease-in-out'
              />
              My Dashboard
            </Button>
          </DrawerClose>
        )}
        <Separator />
        <DrawerClose asChild>
          <Button
            onClick={() => router.push("/orders")}
            variant='ghost'
            className='w-full justify-start font-medium'
          >
            <Truck
              size={14}
              className='mr-3 group-hover:translate-x-1 transition-all duration-300 ease-in-out'
            />
            My Orders
          </Button>
        </DrawerClose>
        <Separator />

        <DrawerClose asChild>
          <Button
            onClick={() => router.push("/quotes")}
            variant='ghost'
            className='w-full justify-start font-medium'
          >
            <ShoppingCart
              size={14}
              className='mr-3 group-hover:translate-x-1 transition-all duration-300 ease-in-out'
            />
            My Quotes
          </Button>
        </DrawerClose>
        <Separator />

        <DrawerClose asChild>
          <Button
            onClick={() => router.push("/account")}
            variant='ghost'
            className='w-full justify-start font-medium'
          >
            <User
              size={14}
              className='mr-3 group-hover:scale-125 transition-all duration-300 ease-in-out'
            />
            My Account
          </Button>
        </DrawerClose>
        <Separator />

        {/* <div className='flex items-center px-3 py-2'>
          <SunMoon size={14} className='mr-3' />
          <span className='text-sm font-medium'>Theme</span>
          <div className='ml-auto'>
            <ThemeToggle variant='ghost' />
          </div>
        </div> */}

        <DrawerClose asChild>
          <Button
            onClick={handleSignOut}
            variant='ghost'
            className='w-full justify-start font-medium text-destructive hover:text-destructive'
          >
            <LogOut
              size={14}
              className='mr-3 group-hover:scale-75 transition-all duration-300 ease-in-out'
            />
            Sign out
          </Button>
        </DrawerClose>
        <Separator />
      </div>
    </div>
  );

  const AvatarButton = () => (
    <Avatar className='flex h-7 w-7 items-center justify-center overflow-hidden'>
      {session.user.image && (
        <Image
          src={session.user.image}
          alt={session.user.firstName || "User profile"}
          fill={true}
          sizes='28px'
          className='object-cover'
          key={session.user.image}
        />
      )}
      {!session.user.image && (
        <AvatarFallback className='flex h-full w-full items-center justify-center bg-gray-100 dark:bg-gray-600'>
          <div className='relative h-full w-full'>
            <Image
              src='/avatar.svg'
              alt='Default avatar'
              fill={true}
              sizes='28px'
              className='object-cover'
            />
          </div>
        </AvatarFallback>
      )}
    </Avatar>
  );

  if (isMobile) {
    return (
      <Drawer>
        <DrawerTrigger className='flex items-center justify-center'>
          <AvatarButton />
        </DrawerTrigger>
        <DrawerContent className='pt-2 pb-8 z-[102]'>
          <MobileContent />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className='flex items-center justify-center'>
        <AvatarButton />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className='w-64 rounded-lg border-muted z-[203] my-2'
        align='end'
      >
        <DesktopContent />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
